export const CONFIG_GUIDE_FILENAME = 'dashboard-configuration-guide.md';

export const CONFIG_GUIDE_CONTENT = `# Custom Dashboard Configuration Guide

This is the reference guide for configuring custom dashboards in Osavul. It is designed for AI assistants (Claude) working with analysts who use MCP SQL tools to explore the database and this guide to build dashboards.

**Workflow:** Analyst describes what they want → Claude uses MCP SQL to explore available tables/columns → Claude writes widget SQL and config following this guide → Dashboard is configured via the UI.

---

## MANDATORY: Workspace Data Isolation

**EVERY widget or filter SQL query MUST restrict data to the current workspace or specific cases.**

When a new dashboard is created, a hidden filter \`global_case_ids\` is automatically added. This filter runs a SQL query like \`SELECT id, name FROM narrative WHERE workspace_id = 1 AND id IN (1, 2, 3)\` (the admin must set the correct workspace ID and narrative IDs) and provides the list of allowed narrative IDs for this workspace. It has "Allow All" enabled, so by default all narratives in the workspace are selected.

A second visible filter \`case_ids\` is also created automatically. It depends on \`global_case_ids\` and lets users narrow down which cases they see.

**Use \`{{case_ids}}\` or \`{{global_case_ids}}\` in every widget and filter SQL to restrict data:**

\`\`\`sql
-- CORRECT: restricted via the default case_ids filter
SELECT * FROM narrative WHERE id = ANY({{case_ids}})

-- CORRECT: restricted via the hidden global_case_ids filter
SELECT * FROM stories WHERE narrative_id = ANY({{global_case_ids}})

-- WRONG: exposes all customers' data
SELECT * FROM narrative
\`\`\`

You can also create your own hidden filters for additional isolation (e.g., by workspace_id directly), but the default \`global_case_ids\` → \`case_ids\` chain is the standard pattern.

---

## Config Architecture

Widget configuration is a JSON object with two tiers:

**Common config fields** apply across multiple widget types. They cover appearance (\`colors\`, \`labels\`, \`legend\`), interaction (\`clickAction\`), data mapping (\`columnMapping\`), and caching. Not every field applies to every type — see the "Applies to" column in the Common Config section below.

**Widget-type-specific config fields** are nested objects that only make sense for one widget type: \`metricConfig\` for \`metric\`, \`cardGridConfig\` for \`card_grid\`, \`tableConfig\` for \`table\`, and \`textConfig\` for \`text\`. Placing a type-specific field on the wrong widget type has no effect.

Quick scope reference:

| Widget type | Type-specific key | Common fields that apply |
|---|---|---|
| \`bar\` | *(none — top-level fields only)* | \`colors\`, \`labels\`, \`legend\`, \`showGrid\`, \`stacked\`, \`logScale\`, \`brushEnabled\`, \`axisBreak\`, \`barDirection\`, \`dataZoom\`, \`echartsOption\`, \`clickAction\`, \`columnMapping\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`line\` | *(none — top-level fields only)* | \`colors\`, \`labels\`, \`legend\`, \`showGrid\`, \`stacked\`, \`logScale\`, \`echartsOption\`, \`clickAction\`, \`columnMapping\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`pie\` | \`pieHole\` *(top-level)* | \`colors\`, \`legend\`, \`echartsOption\`, \`clickAction\`, \`columnMapping\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`table\` | \`tableConfig\`, \`pageSize\` | \`clickAction\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`metric\` | \`metricConfig\` | \`columnMapping\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`card_grid\` | \`cardGridConfig\` | \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`opinions\` | \`opinionsConfig\` | \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`text\` | \`textConfig\` | *(none — only caching)* |
| \`map\` | \`mapConfig\` | \`echartsOption\`, \`clickAction\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |
| \`word_cloud\` | \`wordCloudConfig\` | \`colors\`, \`echartsOption\`, \`clickAction\`, \`columnMapping\`, \`cache_ttl\` / \`cache_type\` / \`cache_refresh_interval\` |

> **Two click mechanisms:** \`bar\`, \`line\`, \`pie\`, \`table\`, \`map\`, and \`word_cloud\` use \`clickAction\` (with \`{key}\`, \`{label}\`, \`{value}\` placeholders — see Click Actions section). \`card_grid\` and \`opinions\` use \`cardGridConfig.clickUrlTemplate\` / \`opinionsConfig.clickUrlTemplate\` instead — a separate field with \`{col_name}\` row-value placeholders. They serve the same user intent but are different APIs. \`metric\` and \`text\` have no click action.

---

## Widget Types

Ten widget types are available. Each has a default SQL template and config. Use these as starting points, then customize with MCP SQL exploration.

### 1. \`bar\` — Bar Chart

Compares categories. Supports multi-series (stacked/grouped).

**SQL must return:** xAxis column + yAxis column. For multi-series: + key column (series grouping) + label column (display name).

**Default SQL template:**
\`\`\`sql
SELECT
    n.id AS id,
    n.name AS label,
    (stat->>'date_publicated')::DATE AS date,
    (stat->>'publications')::INT AS number_of_publications
FROM narrative n
JOIN narrative_evolution_chart_widget necw ON necw.narrative_id = n.id
CROSS JOIN jsonb_array_elements(necw.objects) obj
CROSS JOIN jsonb_array_elements(obj->'stats') stat
WHERE
    n.id = ANY({{case_ids}}) AND
    necw.breakdown = 'NONE' AND
    necw.aggregation = 'DAY' AND
    (stat->>'date_publicated')::DATE >= NOW() - INTERVAL '30 DAY'
ORDER BY date, n.id
\`\`\`

**Default config:**
\`\`\`json
{
  "colors": ["#3B83F6", "#06B6D5", "#A856F7"],
  "labels": { "x": "Date", "y": "Messages count" },
  "legend": { "position": "none" },
  "stacked": true,
  "showGrid": true,
  "barDirection": "vertical",
  "columnMapping": {
    "key": "id",
    "label": "label",
    "xAxis": "date",
    "yAxis": "number_of_publications"
  }
}
\`\`\`

**Bar-specific config fields** (in addition to common fields: \`colors\`, \`labels\`, \`legend\`, \`showGrid\`, \`echartsOption\`, \`clickAction\`, \`columnMapping\`):

| Field | Type | Default | Applies to | Description |
|---|---|---|---|---|
| \`stacked\` | \`boolean\` | \`false\` | bar, line | Stack series instead of grouping side-by-side |
| \`logScale\` | \`boolean\` | \`false\` | bar, line | Logarithmic Y-axis (useful for values spanning orders of magnitude) |
| \`barDirection\` | \`"horizontal" \\| "vertical"\` | \`"vertical"\` | bar only | Bar orientation |
| \`brushEnabled\` | \`boolean\` | \`false\` | bar only | Interactive range selection + dataZoom for zooming |
| \`axisBreak\` | \`{ initialBreaks?: Array<{ start: number; end: number; gap?: string }> }\` | — | bar only | Define breaks on the value axis |
| \`dataZoom\` | \`boolean\` | \`false\` | bar only | Show a slider + inside dataZoom control for panning/zooming across many categories |

---

### 2. \`line\` — Line Chart

Trends over time. Same SQL shape as bar. Supports multi-series.

**Default SQL template:** Same as bar chart (narratives evolution data).

**Default config:**
\`\`\`json
{
  "colors": ["#3B83F6", "#06B6D5", "#A856F7"],
  "labels": { "x": "Date", "y": "Messages count" },
  "legend": { "position": "bottom" },
  "showGrid": true,
  "columnMapping": {
    "key": "id",
    "label": "label",
    "xAxis": "date",
    "yAxis": "number_of_publications"
  },
  "clickAction": {
    "type": "url",
    "url": "https://app.osavul.cloud/narratives/{id}"
  }
}
\`\`\`

**Line-specific config fields** (in addition to all common bar/line fields: \`colors\`, \`labels\`, \`legend\`, \`showGrid\`, \`echartsOption\`, \`clickAction\`, \`columnMapping\`):

| Field | Type | Default | Description |
|---|---|---|---|
| \`stacked\` | \`boolean\` | \`false\` | Stack series instead of overlapping lines |
| \`logScale\` | \`boolean\` | \`false\` | Logarithmic Y-axis |

> \`brushEnabled\`, \`axisBreak\`, and \`barDirection\` are bar-only — they have no effect on line charts.

---

### 3. \`pie\` — Pie/Donut Chart

Proportions. SQL must return exactly two columns: name + numeric value.

**Default SQL template:**
\`\`\`sql
SELECT
    n.name AS label,
    n.total_messages AS total_messages
FROM narrative n
WHERE n.id = ANY({{case_ids}})
\`\`\`

**Default config:**
\`\`\`json
{
  "colors": ["#3B83F6", "#06B6D5", "#A856F7", "#EC4899", "#12B981", "#84CC15"],
  "legend": { "position": "none" },
  "pieHole": 0.5,
  "columnMapping": {
    "name": "label",
    "value": "total_messages"
  }
}
\`\`\`

**Pie-specific:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`pieHole\` | \`number\` | \`0\` | 0 = full pie, 0.4-0.5 = donut |

---

### 4. \`table\` — Data Table

Any columns. Supports server-side pagination and advanced table formatting.

**Default SQL template:**
\`\`\`sql
SELECT n.*
FROM narrative n
WHERE n.id = ANY({{case_ids}})
\`\`\`

**For pagination, add:**
\`\`\`sql
ORDER BY n.date_created DESC
LIMIT {{__limit__}} OFFSET {{__offset__}}
\`\`\`

**Default config:** \`{}\` (no config needed for basic table)

**Full table config example:**
\`\`\`json
{
  "pageSize": 25,
  "tableConfig": {
    "freezeColumnIndex": 1,
    "freezeHeader": true,
    "showFooter": false,
    "columnColors": { "1": "#3B83F6", "4": "#F43F5F" },
    "columnOrder": ["name", "date_created", "total_messages", "status"],
    "columnWidths": { "id": 5, "name": 30, "url": 8, "description": 40 }
  }
}
\`\`\`

> **URL columns:** If your SQL returns a column containing a full URL (e.g. a source link), the table renders it as a clickable link automatically — no extra config needed. Use \`clickAction\` only when you need to compose a navigation URL from non-URL column values (e.g. \`/narratives/{id}\`) or to set a filter on row click.

**tableConfig fields:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`freezeColumnIndex\` | \`number\` | \`0\` | Freeze columns up to this 1-based index |
| \`freezeHeader\` | \`boolean\` | \`true\` | Sticky header row |
| \`freezeFooter\` | \`boolean\` | \`false\` | Sticky footer row |
| \`showFooter\` | \`boolean\` | \`false\` | Show aggregate footer |
| \`pageSize\` | \`number\` | \`10\` | Client-side pagination (ignored when top-level \`pageSize\` is set) |
| \`columnColors\` | \`Record<number, string>\` | — | 1-based column index → hex color |
| \`columnOrder\` | \`string[]\` | — | Column display order by name. Unlisted columns follow after. |
| \`columnWidths\` | \`Record<string, number>\` | — | Column name → width as % of container. Users can resize at runtime. |

**Note:** Top-level \`pageSize\` enables **server-side** pagination (requires \`LIMIT {{__limit__}} OFFSET {{__offset__}}\` in SQL). \`tableConfig.pageSize\` is client-side only and is ignored when server-side is active.

**Click navigation in tables:** If your SQL returns a column that already contains a full URL (e.g. a source link), the table renders it as a clickable link automatically — no \`clickAction\` needed. Use \`clickAction\` only when you need row-click navigation built from a non-URL column value (e.g. \`/narratives/{id}\`), or to drive a filter. Note that table click variables (\`{key}\`, \`{label}\`, \`{value}\`) map to the first and second column values — verify the variables table in the Click Actions section before configuring.

---

### 5. \`metric\` — Single Value Card

A prominent number with sparkline and trend indicator. Works in two modes:
- **Time-series** (multiple rows with date + value): shows sparkline, auto-computes trend
- **Single row**: shows just the value

**Default SQL template:**
\`\`\`sql
SELECT
    (stat->>'date_publicated')::DATE AS date,
    SUM((stat->>'publications')::INT) AS number_of_publications
FROM narrative n
JOIN narrative_evolution_chart_widget necw ON necw.narrative_id = n.id
CROSS JOIN jsonb_array_elements(necw.objects) obj
CROSS JOIN jsonb_array_elements(obj->'stats') stat
WHERE
    n.id = ANY({{case_ids}}) AND
    necw.breakdown = 'NONE' AND
    necw.aggregation = 'DAY' AND
    (stat->>'date_publicated')::DATE >= NOW() - INTERVAL '30 DAY'
GROUP BY date
ORDER BY date
\`\`\`

**Default config:**
\`\`\`json
{
  "metricConfig": {
    "trendRange": 30,
    "mainValueMode": "last"
  },
  "columnMapping": {}
}
\`\`\`

**metricConfig fields:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`trendRange\` | \`number\` | \`7\` | Days for trend window and sparkline range |
| \`trendLabel\` | \`string\` | \`"last {trendRange} days"\` | Custom label after trend percentage |
| \`mainValueMode\` | \`"last" \\| "sum" \\| "avg"\` | \`"last"\` | How to derive the main value from time-series |
| \`sparklineColor\` | \`string\` | First palette color | Sparkline line color (hex) |
| \`showSparkline\` | \`boolean\` | Auto | Force show/hide sparkline. Auto: shown when multiple rows exist. |
| \`valuePrefix\` | \`string\` | — | Prepended to main value (e.g., \`"$"\`) |
| \`valueSuffix\` | \`string\` | — | Appended to main value (e.g., \`"%"\`) |

**Metric columnMapping:**

| Field | Description |
|---|---|
| \`xAxis\` | Date column for sparkline X-axis |
| \`mainValue\` | Override which column is the main numeric value |
| \`trendPercent\` | Column with pre-computed trend % (overrides auto-compute) |

---

### 6. \`card_grid\` — Responsive Card Grid

Cards with date, title, description, sparkline, and metric values.

**Default SQL template:**
\`\`\`sql
SELECT
    e.id "id",
    e.narrative_id "narrative_id",
    e.first_message_date "first_date",
    e.last_message_date "last_date",
    e.title "title",
    ((e.summary_stats->>'publications')::JSONB->>'total')::INT "publications",
    ((e.summary_stats->>'reactions')::JSONB->>'total')::INT "engagement",
    ((e.summary_stats->>'views')::JSONB->>'total')::INT "views",
    (e.opinion_balance->>'sentiment') "sentiment",
    (
        SELECT json_agg(json_build_object('date', daily.d, 'cnt', daily.cnt) ORDER BY daily.d)::TEXT
        FROM (
            SELECT message_date_publicated::DATE AS d, COUNT(*) AS cnt
            FROM topic_modeling_event_messages
            WHERE event_id = e.id
            GROUP BY d
        ) daily
    ) AS "sparkline_data"
FROM topic_modeling_events e
WHERE e.narrative_id = ANY({{case_ids}})
ORDER BY "publications" DESC, "engagement" DESC, "views" DESC
LIMIT 9;
\`\`\`

**Default config:**
\`\`\`json
{
  "cardGridConfig": {
    "dateCol": "first_date",
    "dateEndCol": "last_date",
    "titleCol": "title",
    "sparklineCol": "sparkline_data",
    "sparklineLabel": "Daily publications",
    "clickUrlTemplate": "/topic_reports/{narrative_id}/events/{id}",
    "clickTarget": "_blank",
    "sentimentCol": "sentiment",
    "metrics": [
      { "label": "Publications", "col": "publications", "format": "compact" },
      { "label": "Views", "col": "views", "format": "compact" },
      { "label": "Engagement", "col": "engagement", "format": "compact" }
    ]
  }
}
\`\`\`

> **Click navigation for card_grid:** Use \`cardGridConfig.clickUrlTemplate\` (not \`clickAction\`). \`clickAction\` has no effect on card_grid widgets. See \`clickUrlTemplate\` in the table below.

**cardGridConfig fields:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`dateCol\` | \`string\` | — | Column for card date |
| \`dateEndCol\` | \`string\` | — | Optional end date (renders range: "start — end") |
| \`titleCol\` | \`string\` | — | Column for card title |
| \`descriptionCol\` | \`string\` | — | Column for card description |
| \`sparklineCol\` | \`string\` | — | Column with JSON array for sparkline. Two formats accepted: **preferred** \`[{"date":"2024-01-01","cnt":52},…]\` (enables dates in tooltip) or flat \`[52,49,94]\` (no dates). Produce via \`json_agg(json_build_object('date', d, 'cnt', n))\` in SQL. |
| \`sparklineLabel\` | \`string\` | Column name | Tooltip label for sparkline |
| \`sparklineColor\` | \`string\` | \`"#3B83F6"\` | Sparkline color |
| \`metrics\` | \`Array<{ label, col, format? }>\` | — | Metric definitions. \`format\`: \`"compact"\` (1.2K) or \`"number"\` (1,200). |
| \`showDescription\` | \`boolean\` | \`true\` | Show/hide description |
| \`showMetrics\` | \`boolean\` | \`true\` | Show/hide metrics row |
| \`clickUrlTemplate\` | \`string\` | — | URL on card click. Use a relative path for app pages (e.g., \`"/threats/{id}"\`) or a full URL for external links (e.g., \`"{url}"\` where \`url\` is a column holding the original source URL). \`{col_name}\` placeholders are replaced with row values; full URLs (starting with \`http://\` or \`https://\`) are used as-is, all other values are URL-encoded. |
| \`clickTarget\` | \`"_blank" \\| "_self"\` | \`"_blank"\` | Link target |
| \`topicsCol\` | \`string\` | — | Column with topics (JSON array or CSV) |
| \`tagsCol\` | \`string\` | — | Column with tags (JSON array or CSV) |
| \`showBadges\` | \`boolean\` | Auto | Show topics/tags badges |
| \`sentimentCol\` | \`string\` | — | Column containing a sentiment label displayed as a colored metric value alongside other metrics. Accepted values (case-insensitive): \`positive\`, \`negative\`, \`neutral\`, \`mostly_positive\`, \`mostly_negative\`, \`controversial\`. Unknown values are silently ignored. **For topic events:** use \`opinion_balance->>'sentiment'\` in SQL — value is pre-computed. **For narratives:** write a \`CASE\` expression against \`narrative_sentiment_widget\` shares that returns one of the accepted string values. |
| \`cardsPerPage\` | \`number\` | \`9\` | Number of cards shown per page. The widget only paginates rows already returned by the query — increase the SQL \`LIMIT\` to match, otherwise pages beyond the SQL's row count will be empty. |
| \`sentimentBackground\` | \`boolean\` | \`false\` | Tint each card's background with a washed-out color matching its sentiment. Requires \`sentimentCol\`. |
| \`cardBackground\` | \`string\` | — | CSS background color applied to every card, e.g. \`"#FEF9C3"\` or \`"rgba(59,131,246,0.08)"\`. Low-alpha \`rgba\` values are recommended so cards stay readable in the dark theme. Combines with \`sentimentBackground\` (the sentiment tint layers on top). |
| \`cardBackgroundCol\` | \`string\` | — | Column whose value is a CSS color used as that card's background. Rows with an empty/\`NULL\` value fall back to \`cardBackground\` (or the default). Produce per-card colors in SQL, e.g. \`CASE WHEN severity = 'high' THEN '#FEE2E2' WHEN severity = 'medium' THEN '#FEF9C3' END AS card_color\`, then set \`"cardBackgroundCol": "card_color"\`. |

---

### 7. \`opinions\` — Opinions Grid

Cards showing topic-modeling opinions: a sentiment icon, title, and metric values (e.g. rating, publications, actors). No date, description, sparkline, or topic/tag badges.

**Default SQL template:**
\`\`\`sql
SELECT
    o.id "id",
    o.narrative_id "narrative_id",
    o.event_id "event_id",
    o.title "title",
    LOWER(o.sentiment) "sentiment",
    ROUND(o.rating)::INT "rating",
    o.messages_count "publications",
    (
        SELECT COUNT(DISTINCT om.actor_id)
        FROM topic_modeling_opinion_messages om
        WHERE om.opinion_id = o.id AND om.is_deleted = false
    ) "actors"
FROM topic_modeling_opinions o
WHERE o.narrative_id = ANY({{case_ids}})
ORDER BY o.rating DESC NULLS LAST
LIMIT 9;
\`\`\`

**Default config:**
\`\`\`json
{
  "opinionsConfig": {
    "titleCol": "title",
    "sentimentCol": "sentiment",
    "clickUrlTemplate": "/topic_reports/{narrative_id}/events/{event_id}",
    "clickTarget": "_blank",
    "metrics": [
      { "label": "Rating", "col": "rating", "format": "compact", "tooltip": "Rating based on the number of reactions and comments" },
      { "label": "Publications", "col": "publications", "format": "compact" },
      { "label": "Actors", "col": "actors", "format": "compact" }
    ]
  }
}
\`\`\`

> **Click navigation for opinions:** Use \`opinionsConfig.clickUrlTemplate\` (not \`clickAction\`). \`clickAction\` has no effect on opinions widgets.

**opinionsConfig fields:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`titleCol\` | \`string\` | Auto-detected | Column for the card title |
| \`sentimentCol\` | \`string\` | Auto-detected (column matching \`sentiment\`) | Column with the sentiment value (case-insensitive). The sentiment **icon** only distinguishes \`"positive"\`, \`"negative"\`, or falls back to neutral. The \`sentimentBackground\` **tint** (below) supports all six normalized values: \`positive\`, \`negative\`, \`neutral\`, \`mostly_positive\`, \`mostly_negative\`, \`controversial\`. |
| \`metrics\` | \`Array<{ label, col, format?, tooltip? }>\` | — | Metric definitions shown on each card. \`format\`: \`"compact"\` (1.2K) or \`"number"\` (1,200). \`tooltip\` shows an info icon with hint text next to the metric. |
| \`showMetrics\` | \`boolean\` | \`true\` | Show/hide the metrics row |
| \`clickUrlTemplate\` | \`string\` | — | URL on card click. Same \`{col_name}\` placeholder rules as \`cardGridConfig.clickUrlTemplate\` (relative paths are URL-encoded; full \`http(s)\` URLs are used as-is). |
| \`clickTarget\` | \`"_blank" \\| "_self"\` | \`"_blank"\` | Link target |
| \`cardsPerPage\` | \`number\` | \`9\` | Number of cards shown per page. The widget only paginates rows already returned by the query — increase the SQL \`LIMIT\` to match, otherwise pages beyond the SQL's row count will be empty. |
| \`sentimentBackground\` | \`boolean\` | \`false\` | Tint each card's background with a washed-out color matching its sentiment. Requires \`sentimentCol\`. |

---

### 8. \`map\` — World Map / Country Regions

Choropleth or presence map. Four map types via \`mapConfig.mapType\`:

- \`"world"\` (default) — countries matched by **ISO 3166-1 alpha-2 code** (two-letter, e.g. \`'US'\`, \`'DE'\`, \`'UA'\`)
- \`"ukraine"\` — oblasts matched by **ISO 3166-2 code** (e.g. \`'UA-63'\` Kharkiv, \`'UA-43'\` Crimea)
- \`"germany"\` — Bundesländer matched by **ISO 3166-2 code** (e.g. \`'DE-BY'\` Bavaria)
- \`"russia"\` — two levels via \`federalLevel\`: \`"subjects"\` (default) — the 83 internationally recognized federal subjects matched by **ISO 3166-2 code** (e.g. \`'RU-MOW'\` Moscow, \`'RU-NVS'\` Novosibirsk Oblast); \`"districts"\` — the 8 federal districts matched by **RU-CFO-style codes**: \`RU-CFO\` Central, \`RU-SZFO\` Northwestern, \`RU-YUFO\` Southern, \`RU-SKFO\` North Caucasian, \`RU-PFO\` Volga, \`RU-UFO\` Ural, \`RU-SFO\` Siberian, \`RU-DFO\` Far Eastern

Regional maps show neighboring countries as a non-interactive gray backdrop and city-name markers (\`showCities\`).

**Russia map borders:** only the internationally recognized territory of the Russian Federation is rendered. Crimea, Sevastopol and the occupied parts of Ukraine's Donetsk, Luhansk, Zaporizhzhia and Kherson oblasts are **not** part of any Russian subject or district — they are shown as part of Ukraine in the gray context backdrop. Data rows with codes for those territories (e.g. \`RU-CR\`) will not match and are ignored. The Kaliningrad exclave is included by default and can be removed with \`"showKaliningrad": false\` (at district level this removes the exclave part of \`RU-SZFO\`).

**SQL must return:** a code column (ISO-2 for world, ISO 3166-2 for regional maps) and optional numeric columns for tooltip and choropleth intensity.

**Default SQL template:**
\`\`\`sql
SELECT
    c.code AS country_code,
    SUM(na.messages_count) AS publications,
    COUNT(DISTINCT na.actor_id) AS actors,
    COUNT(DISTINCT CASE WHEN sa.source_id IS NOT NULL THEN s.id END) AS state_affiliated_actors,
    COUNT(DISTINCT CASE WHEN s.is_bot IS TRUE THEN s.id END) AS inauthentic_actors,
    COUNT(DISTINCT CASE WHEN sd.source_id IS NOT NULL THEN s.id END) AS high_risk_actors
FROM narrative_actors na
JOIN source s ON s.id = na.actor_id
JOIN country c ON c.id = s.origin_country_id
LEFT JOIN (SELECT DISTINCT source_id FROM source_affiliation) sa ON sa.source_id = s.id
LEFT JOIN (SELECT DISTINCT source_id FROM source_discreditation) sd ON sd.source_id = s.id
WHERE na.narrative_id = ANY({{case_ids}})
GROUP BY c.code
ORDER BY 2 DESC;
\`\`\`

**Default config:**
\`\`\`json
{
  "mapConfig": {
    "countryColumn": "country_code",
    "valueColumn": "publications",
    "tooltipColumns": ["publications", "actors", "state_affiliated_actors", "inauthentic_actors", "high_risk_actors"],
    "tooltipLabels": {
      "publications": "Publications",
      "actors": "Actors",
      "state_affiliated_actors": "State-affiliated actors",
      "inauthentic_actors": "Inauthentic actors",
      "high_risk_actors": "High-risk actors"
    },
    "colorRange": ["#dbeafe", "#1d4ed8"],
    "emptyColor": "#e5e7eb",
    "roam": true
  }
}
\`\`\`

**mapConfig fields:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`mapType\` | \`"world" \\| "ukraine" \\| "germany" \\| "russia"\` | \`"world"\` | Which map to render |
| \`federalLevel\` | \`"subjects" \\| "districts"\` | \`"subjects"\` | Russia map only: render the 83 federal subjects or the 8 federal districts |
| \`showKaliningrad\` | \`boolean\` | \`true\` | Russia map only: include the Kaliningrad exclave |
| \`countryColumn\` | \`string\` | First column | SQL column containing ISO-2 country codes (world) or ISO 3166-2 / district codes (regional maps) |
| \`showCities\` | \`boolean\` | \`true\` (\`false\` when \`labelColumn\` set) | City-name markers on regional maps (ignored for world). An explicit value always wins |
| \`labelColumn\` | \`string\` | — | Column whose raw value is drawn on each region/country (e.g. \`"5.8%"\`, a number, any string). Shapes without data get no label. Works on all map types including world. On regional maps hides city markers by default — set \`showCities: true\` to show both |
| \`sentimentColumn\` | \`string\` | — | Column with sentiment values \`positive \\| negative \\| neutral \\| mostly_positive \\| mostly_negative \\| controversial\`. Fills regions/countries with the app's sentiment colors and hides the choropleth legend (\`valueColumn\` then only feeds the tooltip); unrecognized values leave the shape at \`emptyColor\`. Works on all map types including world |
| \`showSentimentLegend\` | \`boolean\` | \`true\` | Show a bottom-left sentiment color legend when \`sentimentColumn\` is set. Lists only sentiments present in the data |
| \`seaColor\` | \`string\` | \`"#e9f0f7"\` | Background (sea) color on regional maps (ignored for world) |
| \`regionMapping\` | \`Record<string, string>\` | — | Data value → code translation applied before the join, e.g. \`{ "Kharkiv branch": "UA-63" }\`. Rows mapped to the same code have their numeric columns summed |
| \`valueColumn\` | \`string\` | — | Numeric column for choropleth fill intensity. Omit for a **presence map** where all data countries share one color |
| \`tooltipColumns\` | \`string[]\` | All non-country columns | Columns shown in the hover tooltip, in order |
| \`tooltipLabels\` | \`Record<string, string>\` | Column name | Display label per column, e.g. \`{ "pub_count": "Publications" }\` |
| \`colorRange\` | \`[string, string]\` | \`["#dbeafe", "#1d4ed8"]\` | Choropleth gradient: \`[low-color, high-color]\` |
| \`emptyColor\` | \`string\` | \`"#e5e7eb"\` | Fill color for countries with no data |
| \`roam\` | \`boolean\` | \`true\` | Enable scroll-wheel zoom and drag-to-pan |

**Regional map example (Ukraine oblasts):** regional actor groups aggregated per oblast via \`regionMapping\`. The SQL returns group IDs; the mapping translates them to ISO 3166-2 codes. Several groups mapped to the same code are summed; values with no mapping and no matching polygon (like \`104\` here) are silently ignored.

\`\`\`sql
SELECT
    sg.group_id::text AS group_id,
    COUNT(DISTINCT sg.source_id) AS actors,
    COALESCE(SUM(psm.last_7d_engagement), 0)::bigint AS engagement_7d
FROM source_group sg
LEFT JOIN precalculated_source_metrics psm ON psm.source_id = sg.source_id
WHERE sg.group_id IN (101, 102, 103, 104)
  AND sg.workspace_id = {{ws}}::int
GROUP BY sg.group_id
ORDER BY engagement_7d DESC
\`\`\`

\`\`\`json
{
  "mapConfig": {
    "mapType": "ukraine",
    "countryColumn": "group_id",
    "valueColumn": "engagement_7d",
    "tooltipColumns": ["actors", "engagement_7d"],
    "tooltipLabels": { "actors": "Actors", "engagement_7d": "Engagement (7d)" },
    "regionMapping": {
      "101": "UA-63",
      "102": "UA-63",
      "103": "UA-51"
    }
  }
}
\`\`\`

**Russia map example (federal districts):** the SQL returns ISO 3166-2 subject codes; \`regionMapping\` rolls them up to district codes (rows mapped to the same district are summed). With \`federalLevel: "subjects"\` you would skip the mapping and let the subject codes join directly.

\`\`\`json
{
  "mapConfig": {
    "mapType": "russia",
    "federalLevel": "districts",
    "countryColumn": "region_code",
    "valueColumn": "publications",
    "tooltipColumns": ["publications", "actors"],
    "tooltipLabels": { "publications": "Publications", "actors": "Actors" },
    "regionMapping": {
      "RU-MOW": "RU-CFO",
      "RU-MOS": "RU-CFO",
      "RU-SPE": "RU-SZFO",
      "RU-NVS": "RU-SFO"
    }
  }
}
\`\`\`

**Notes:**
- \`{{ws}}\` above is **not** a built-in placeholder — it stands for a custom hidden filter you create for workspace isolation (see [Workspace Data Isolation](#mandatory-workspace-data-isolation), "create your own hidden filters"). Add a hidden filter named \`ws\` that resolves to the current workspace ID, or swap it for a literal \`workspace_id\`. Leaving \`{{ws}}\` undefined makes the query fail validation rather than silently returning another workspace's data.
- World map codes must be ISO 3166-1 alpha-2 (two-letter); regional map codes must be ISO 3166-2 (\`UA-63\`, \`DE-BY\`, \`RU-MOW\`), except the Russia districts level which uses RU-CFO-style codes (\`RU-SZFO\`, \`RU-DFO\`, …). Other formats will not match — use \`regionMapping\` to translate.
- **On-region labels:** set \`labelColumn\` to draw a value directly on each region or country — the column's raw value is shown as-is, so format in SQL (e.g. \`ROUND(share * 100, 1) || '%' AS share_label\`). Numbers are compact-formatted (1.2K). Works on all map types including world.
- **Sentiment coloring:** set \`sentimentColumn\` to color regions or countries by sentiment instead of the value gradient — works on the world map too (assign a sentiment per country code). The column must return \`positive\`, \`negative\`, \`neutral\`, \`mostly_positive\`, \`mostly_negative\` or \`controversial\` (case-insensitive; e.g. \`LOWER(o.sentiment)\`). Hovering shows the full-strength sentiment color. If the sentiment column is also listed in \`tooltipColumns\`, the tooltip shows its human-readable form (\`mostly_negative\` → "Mostly negative"). Combine with \`labelColumn\` to show the value on top, e.g.:

\`\`\`json
{
  "mapConfig": {
    "mapType": "ukraine",
    "countryColumn": "region_code",
    "sentimentColumn": "sentiment",
    "labelColumn": "share_label",
    "tooltipColumns": ["publications", "sentiment"]
  }
}
\`\`\`
- Hovering a country/region highlights it and shows a tooltip with the configured columns.
- \`clickAction\` works on map — clicking a country/region triggers the action with \`{key}\` = its code and \`{value}\` = the value column value. Extra row columns are available as \`{column_name}\` placeholders.
- Use \`echartsOption\` for raw ECharts overrides (e.g. to reposition the \`visualMap\` legend or adjust the series projection).
- **Map data attribution:** Ukraine oblast and Russia federal subject boundaries © [geoBoundaries](https://www.geoboundaries.org) (CC BY 4.0); Germany state boundaries © GeoBasis-DE / [deutschlandGeoJSON](https://github.com/isellsoap/deutschlandGeoJSON) (dl-de/by-2-0); context-country outlines from [world-atlas](https://github.com/topojson/world-atlas) (Natural Earth, public domain).

---

### 9. \`word_cloud\` — Word / Emoji Cloud

Most frequent keywords and emojis at a glance. Each token is drawn once; its size shows how often it is mentioned, and its color shows frequency, trend (rising or falling) or sentiment, depending on what the SQL provides. SQL returns one row per token: token text + numeric frequency, plus optional columns for the kind (\`keyword\` / \`emoji\`), the trend, the sentiment, and the sample size.

Viewers get a **Keywords / Emojis / Both** switch inside the widget whenever the data contains both kinds (hide it with \`showModeToggle: false\`). Emojis are detected from the token text automatically — flags (🇺🇦), keycaps (1️⃣), skin tones and ZWJ sequences all count as emoji. Set \`kindColumn\` to override detection with your own column.

**What viewers can do** (no configuration needed):

- **Color** — when the data carries a trend and/or a sentiment, a selector switches what color shows: Frequency, Trend or Sentiment. A legend explains the current one.
- **Cloud / List** — switch to a ranked table with exact counts, a bar per word, the trend and the sentiment. The list is the keyboard- and screen-reader-friendly view of the same data.
- **Hide a word** — right-click a word in the cloud, or press the × button in the list. Hidden words are remembered per widget in that viewer's browser and listed above the chart, where one click brings a word back. In edit mode an admin also gets **Add to stop words**, which moves the hidden words into \`wordCloudConfig.stopWords\` for everyone (saved with the dashboard).
- **Click a word** — runs the widget's \`clickAction\`, in both views.

**Default SQL template:**
\`\`\`sql
-- Top 50 keywords + top 50 emojis from the newest ~1000 messages of the chosen cases.
-- The 1000-message budget is split equally between cases; cost grows linearly with it.
-- The messages of each case are cut into a newer and an older half to tell what is rising.
-- Sentiment is the message-level value stored on the message row itself.
WITH picked AS (
    SELECT s.message_id, min(s.half) AS half
    FROM unnest({{case_ids}}::integer[]) AS c(id)
    CROSS JOIN LATERAL (
        SELECT v.message_id, ntile(2) OVER (ORDER BY v.message_id DESC) AS half
        FROM (
            SELECT u.message_id
            FROM (
                (SELECT nm.message_id
                 FROM narrative_message nm
                 WHERE nm.narrative_id = c.id AND nm.status = 'NEW'
                 ORDER BY nm.message_id DESC
                 LIMIT GREATEST(1000 / NULLIF(cardinality({{case_ids}}::integer[]), 0), 5))
                UNION ALL
                (SELECT nm.message_id
                 FROM narrative_message nm
                 WHERE nm.narrative_id = c.id AND nm.status = 'READ'
                 ORDER BY nm.message_id DESC
                 LIMIT GREATEST(1000 / NULLIF(cardinality({{case_ids}}::integer[]), 0), 5))
            ) u
            ORDER BY u.message_id DESC
            LIMIT GREATEST(1000 / NULLIF(cardinality({{case_ids}}::integer[]), 0), 5)
        ) v
    ) s
    GROUP BY s.message_id
),
msgs AS MATERIALIZED (
    SELECT p.half, upper(m.sentiment) AS sentiment, left(m.text, 1500) AS txt
    FROM picked p
    JOIN message m ON m.id = p.message_id
),
sizes AS (
    SELECT count(*) AS total,
           count(*) FILTER (WHERE half = 1) + 1.0 AS newer,
           count(*) FILTER (WHERE half = 2) + 1.0 AS older
    FROM msgs
),
words AS (
    SELECT lower(t.token) AS name,
           count(*) AS value,
           count(*) FILTER (WHERE msgs.half = 1) AS recent,
           count(*) FILTER (WHERE msgs.sentiment = 'POSITIVE') AS pos,
           count(*) FILTER (WHERE msgs.sentiment = 'NEGATIVE') AS neg,
           count(msgs.sentiment) AS rated
    FROM msgs, LATERAL ts_parse('default', msgs.txt) AS t
    WHERE t.tokid IN (1, 2) AND char_length(t.token) >= 4
    GROUP BY 1
    ORDER BY 2 DESC
    LIMIT 300
),
top_words AS (
    SELECT name, value, recent, pos, neg, rated
    FROM words
    -- stop words: built-in English and Russian dictionaries, plus a manual Ukrainian list
    WHERE ts_lexize('english_stem', name) <> '{}'
      AND ts_lexize('russian_stem', name) <> '{}'
      AND name <> ALL (ARRAY[
        'його','їхні','їхній','тому','може','можуть','можна','чому','коли','куди','який','яка','яке','які','яких','якій','якого','якому','якщо','якби',
        'було','була','були','буде','будуть','бути','вони','вона','воно','вами','нами','мене','мені','тебе','тобі','себе','собі','йому','нього','ними',
        'також','теж','після','перед','проти','через','серед','біля','щодо','понад','близько','крім','саме','зараз','тепер','тоді','потім','знову','завжди','ніколи',
        'треба','потрібно','більше','менше','дуже','майже','просто','далі','лише','тільки','навіть','хоча','адже','тобто','отже','однак','проте','поки','щоб','щоби',
        'цього','цьому','цієї','цей','того','такий','така','таке','такі','таких','весь','всіх','всім','свої','своїх','свого','своєї','свою','наші','наша','ваші',
        'інші','інших','інший','інша','один','одна','одне','одного','кожен','кожна','щось','хтось','десь','нічого','немає','зокрема','разом','зовсім',
        'сьогодні','вчора','завтра','року','році','роки','років','має','мають','мати',
        'также','очень','который','которые','которая','которых','этих','своих','свои','будут','могут','именно','однако','пока','году','года',
        'http','https','www'])
    ORDER BY value DESC
    LIMIT 50
),
top_emojis AS (
    SELECT e[1] AS name,
           count(*) AS value,
           count(*) FILTER (WHERE msgs.half = 1) AS recent,
           count(*) FILTER (WHERE msgs.sentiment = 'POSITIVE') AS pos,
           count(*) FILTER (WHERE msgs.sentiment = 'NEGATIVE') AS neg,
           count(msgs.sentiment) AS rated
    FROM msgs, LATERAL regexp_matches(msgs.txt, '[\\U0001F1E6-\\U0001F1FF]{2}|[\\U0001F300-\\U0001F3FA\\U0001F400-\\U0001FAFF\\U00002600-\\U000027BF\\U00002B00-\\U00002BFF]', 'g') AS e
    GROUP BY 1
    ORDER BY 2 DESC
    LIMIT 50
),
tokens AS (
    SELECT name, value, recent, pos, neg, rated, 'keyword' AS kind FROM top_words
    UNION ALL
    SELECT name, value, recent, pos, neg, rated, 'emoji' AS kind FROM top_emojis
)
SELECT t.name,
       t.value,
       t.kind,
       -- share in the newer half against the older half: 0.4 = +40%. Rare tokens stay at 0.
       CASE WHEN t.value >= 10
            THEN round((((t.recent + 1.0) / s.newer) / ((t.value - t.recent + 1.0) / s.older) - 1)::numeric, 2)
            ELSE 0
       END AS trend,
       -- net sentiment from -1 to 1 over the rated mentions; empty when fewer than 10 are rated
       CASE WHEN t.rated >= 10
            THEN round((t.pos - t.neg)::numeric / t.rated, 2)
       END AS sentiment,
       s.total AS sample_size
FROM tokens t
CROSS JOIN sizes s
ORDER BY t.value DESC;
\`\`\`

> **Performance:** the message tables are huge, so the template never scans a whole case. It reads the newest messages through the \`(narrative_id, status, message_id)\` index, shares a fixed budget of ~1000 messages between the chosen cases, and tokenises only that sample. Measured on production data: ~0.2 s when the pages are cached, ~10 s on a cold cache (about 10 ms of disk reads per sampled message). Raise the budget only together with caching (\`cache_ttl\`, ideally \`cache_type: "stale_while_revalidate"\`). Keep SQL comments free of words such as SET, DO, SHOW or DROP — the keyword filter also reads comments.

**Default config:**
\`\`\`json
{
  "columnMapping": { "name": "name", "value": "value" },
  "wordCloudConfig": {
    "mode": "both",
    "kindColumn": "kind",
    "trendColumn": "trend",
    "sentimentColumn": "sentiment",
    "sampleColumn": "sample_size",
    "showModeToggle": true,
    "maxWords": 100,
    "minWordLength": 3,
    "stopWords": [],
    "shape": "circle"
  }
}
\`\`\`

**wordCloudConfig fields:**

| Field | Type | Default | Description |
|---|---|---|---|
| \`mode\` | \`"keywords" \\| "emojis" \\| "both"\` | \`"both"\` | Which tokens are shown when the widget loads |
| \`showModeToggle\` | \`boolean\` | \`true\` | Show the Keywords / Emojis / Both switch to viewers (only rendered when both kinds are present in the data) |
| \`kindColumn\` | \`string\` | — | Column holding \`"keyword"\` / \`"emoji"\` per row. When omitted, emojis are detected from the token text |
| \`view\` | \`"cloud" \\| "list"\` | \`"cloud"\` | View shown when the widget loads |
| \`showViewToggle\` | \`boolean\` | \`true\` | Show the Cloud / List switch to viewers |
| \`allowHide\` | \`boolean\` | \`true\` | Let viewers hide words |
| \`trendColumn\` | \`string\` | — | Column that says how much each word grew or shrank between two periods, as a fraction: \`0.4\` = 40% more mentions, \`-0.25\` = 25% fewer, \`0\` = no change, \`NULL\` = unknown. When set, words can be colored by trend: blue = growing, red = shrinking, gray = about the same |
| \`sentimentColumn\` | \`string\` | — | Column with each token's net sentiment from \`-1\` (all negative) to \`1\` (all positive), or a label \`positive\` / \`neutral\` / \`negative\`. \`NULL\` = unknown. Turns on sentiment coloring |
| \`sentimentThreshold\` | \`number\` | \`0.15\` | Net sentiment needed before a token counts as positive or negative |
| \`colorBy\` | \`"frequency" \\| "trend" \\| "sentiment"\` | trend if available, then sentiment, else frequency | What color encodes when the widget loads. Viewers can switch with the Color selector |
| \`showColorToggle\` | \`boolean\` | \`true\` | Show the Color selector (only rendered when the data supports more than one encoding) |
| \`trendThreshold\` | \`number\` | \`0.3\` | How big the change must be before a word counts as rising or falling (0.3 = 30%). Smaller changes show as steady, which keeps random noise from coloring everything |
| \`trendLabel\` | \`string\` | \`"vs last week"\` | Short text telling viewers which two periods were compared, e.g. "vs previous month"; shown after the legend and in the tooltip. The built-in template compares the newer half of the fetched messages with the older half, so it uses "newer vs older half of the sample" |
| \`sampleColumn\` | \`string\` | — | Column with the number of messages the counts are based on (same value in every row). Shown as the caption "Based on the latest N messages" |
| \`caption\` | \`string\` | — | Caption text; replaces the automatic one |
| \`maxWords\` | \`number\` | \`100\` | Maximum tokens drawn; the highest values win |
| \`minWordLength\` | \`number\` | \`2\` | Keywords shorter than this are dropped (emojis are never dropped by length) |
| \`stopWords\` | \`string[]\` | \`[]\` | Keywords to hide, case-insensitive. Handy for stop words the SQL did not remove |
| \`sizeRange\` | \`[number, number]\` | auto | Font size range in px for the smallest and largest value. By default the range is fitted to the widget size and the number of words, so the cloud fills the card; set it only to force fixed sizes |
| \`minEmojiSize\` | \`number\` | auto | Smallest font size for emojis in px. Emojis are single glyphs and usually have far lower counts than keywords, so they get a higher floor than words: by default 45% of the top size, between 20 and 32. Raise it if emojis still look small next to words |
| \`shape\` | \`string\` | \`"circle"\` | Layout shape: \`"circle"\`, \`"cardioid"\`, \`"diamond"\`, \`"triangle-forward"\`, \`"triangle"\`, \`"pentagon"\`, \`"star"\` |
| \`rotate\` | \`boolean\` | \`false\` | Allow words to be rotated ±90° |
| \`gridSize\` | \`number\` | \`8\` | Layout grid step in px; larger values space the words out |

**Sizes and colors:** a word's area is proportional to its frequency (font size follows the square root of the value), so one dominant word does not shrink the rest to the minimum. In frequency mode color stays flat so the same number is not encoded twice: every word uses the first entry of \`colors\` (default: blue); emojis keep their own colors. \`useSentimentColors\` does not apply.

**Trend coloring:** with \`trendColumn\` set, color answers "what is gaining or losing traction" instead: blue = rising, red = falling, gray = steady, and the legend lists all three. The list view and the tooltip also show the arrow and the percentage. The template computes the trend inside the sample: each case's newest messages are cut into a newer and an older half, and a token's share in the newer half is compared with its share in the older one (with +1 smoothing, and tokens seen fewer than 10 times fixed at 0). It therefore means "rising within the latest ~1000 messages", not week over week, and the template sets \`trendLabel\` to "newer vs older half of the sample" to say so. If you compute your own trend (for example two date ranges), make the two periods the same size or compare shares, return the relative change in \`trendColumn\`, and set \`trendLabel\` to what you compared (the default is "vs last week").

**Sentiment coloring:** with \`sentimentColumn\` set, viewers can color words by the tone of the messages they appear in: green = positive, gray = neutral, red = negative. The template takes the message-level sentiment from \`message.sentiment\` (\`POSITIVE\` / \`NEUTRAL\` / \`NEGATIVE\`, present on nearly every message) and returns the net score (positive mentions minus negative mentions, divided by the mentions that have a sentiment). Do not use \`narrative_message.sentiment\` for this: that per-case column is empty for most cases. A token with fewer than 10 rated mentions gets \`NULL\` and shows as "–" in the list, and the Sentiment option disappears when no token has a value. Note that this is the tone of the messages a word appears in, not the tone of the word itself.

**Hiding and the row limit:** the SQL decides how many tokens exist (50 + 50 in the template); hiding a word does not pull the next one in. If viewers hide words often, raise the two \`LIMIT 50\` lines.

**Click action:** \`{key}\` and \`{label}\` are the token text, \`{value}\` is its frequency, and every column of the row is available as \`{column_name}\` — e.g. \`set_filter\` on a \`text\` filter to drill into messages containing the clicked word.

---

## Text Widget

No SQL query required. Content is written in the **Content** tab of the widget editor using Markdown.

Supported formatting: **Bold**, *Italic*, Headings, Blockquote, Bullet list, Numbered list, Link.

### textConfig

| Field | Type | Default | Description |
|---|---|---|---|
| \`backgroundColor\` | \`string\` | transparent | Background fill (any CSS color, e.g. \`"#f9fafb"\`) |
| \`textColor\` | \`string\` | inherited | Text color (any CSS color, e.g. \`"#111827"\`) |

\`\`\`json
{
  "textConfig": {
    "backgroundColor": "#f9fafb",
    "textColor": "#111827"
  }
}
\`\`\`

---

## Common Config Fields

Fields shared by multiple widget types. Always check the **Applies to** column — fields outside their scope are silently ignored.

| Field | Type | Default | Applies to | Description |
|---|---|---|---|---|
| \`colors\` | \`string[]\` | Dashboard palette | bar, line, pie, word_cloud | Override color palette applied in order to series/slices. For word_cloud only the first entry is used: the text color in frequency mode (default: blue) |
| \`useSentimentColors\` | \`boolean\` | \`false\` | bar, line, pie | Map series/slice names to sentiment colors: POSITIVE → \`#22C55F\`, NEUTRAL → \`#FFCC33\`, NEGATIVE → \`#EF4444\` |
| \`labels\` | \`{ x?, y?, title? }\` | — | bar, line, pie | Axis labels and chart title |
| \`legend\` | \`{ position }\` | — | bar, line, pie | \`"top"\`, \`"bottom"\`, \`"left"\`, \`"right"\`, \`"none"\` |
| \`showGrid\` | \`boolean\` | \`false\` | bar, line | Grid lines |
| \`echartsOption\` | \`Record<string, unknown>\` | — | bar, line, pie, map, word_cloud | Raw ECharts option deep-merged into chart config. For advanced customization. |
| \`clickAction\` | \`WidgetClickAction\` | — | bar, line, pie, table, map, word_cloud | Action on element click. **Not** for metric, card_grid, opinions, or text. card_grid and opinions use \`cardGridConfig.clickUrlTemplate\` / \`opinionsConfig.clickUrlTemplate\` instead. |
| \`columnMapping\` | \`ColumnMapping\` | — | bar, line, pie, metric, word_cloud | Map SQL column names to chart roles (see Column Mapping). Ignored by table, card_grid, text. |
| \`cache_ttl\` | \`number\` | \`0\` | **all types** | Cache lifetime in seconds. \`0\` = disabled. See **Caching** section below. |
| \`cache_type\` | \`string\` | \`"blocking"\` | **all types** | \`"blocking"\`, \`"stale_while_revalidate"\`, or \`"background_update"\`. See **Caching** section below. |
| \`cache_refresh_interval\` | \`number\` | — | **all types** | Seconds between automatic background refreshes. Only used with \`cache_type: "background_update"\`. Defaults to \`cache_ttl\` if omitted. **Minimum \`3600\`** (1 hour) — lower values are rejected on save. |

> **Note:** Series or slices named \`"Other"\` (case-insensitive) always receive \`#64758B\` across all chart types, regardless of palette or sentiment settings. This is built-in behavior and cannot be overridden via config (use the \`colors\` array override if needed).

**Default color palette:** \`["#3B83F6", "#06B6D5", "#A856F7", "#EC4899", "#12B981", "#84CC15", "#F59E0C", "#FA7315", "#F43F5F"]\`

---

## Column Mapping Reference

Used to override default positional column detection. If omitted, charts use: 1st column = x-axis/key, 2nd = y-axis/value, 3rd = series name.

| Field | Used by | Description |
|---|---|---|
| \`xAxis\` | bar, line, metric | X-axis / date column |
| \`yAxis\` | bar, line | Y-axis value column |
| \`key\` | bar, line | Series grouping column (each unique value = separate series) |
| \`label\` | bar, line | Display label for series (shown in legends/tooltips instead of key) |
| \`name\` | pie, word_cloud | Slice name / token column |
| \`value\` | pie, word_cloud | Slice value / token frequency column |
| \`mainValue\` | metric | Main numeric value column |
| \`trendPercent\` | metric | Pre-computed trend % column |

---

## Click Actions

**Applies to:** \`bar\`, \`line\`, \`pie\`, \`table\`, \`map\`, \`word_cloud\`. For \`card_grid\`, use \`cardGridConfig.clickUrlTemplate\` instead; for \`opinions\`, use \`opinionsConfig.clickUrlTemplate\`. \`metric\` and \`text\` have no click action.

> **Table tip:** If a SQL column already contains a full URL, the table renders it as a link automatically — no \`clickAction\` needed. Use \`clickAction\` only when you need to compose a URL from non-URL column values, or to set a filter on row click.

### \`url\` — Navigate to URL
\`\`\`json
{
  "clickAction": {
    "type": "url",
    "url": "https://app.osavul.cloud/narratives/{id}",
    "target": "_blank"
  }
}
\`\`\`

Placeholders replaced from clicked data point:
- \`{key}\` — primary identifier (x-axis value for bar/line, slice name for pie, first column for table)
- \`{label}\` — display label
- \`{value}\` — numeric value
- \`{column_name}\` — any column from the SQL result row

### \`set_filter\` — Drill-down
\`\`\`json
{
  "clickAction": {
    "type": "set_filter",
    "parameterName": "selected_narrative",
    "valueTemplate": "{key}"
  }
}
\`\`\`

Clicking sets the named filter to the resolved value. Enables interactive drill-down between widgets.

---

## SQL Syntax — Filter Patterns

### \`select\` / \`multi_select\` → \`= ANY({{param}})\`

Values are ALWAYS arrays. **MUST** use \`= ANY()\`:

\`\`\`sql
WHERE n.id = ANY({{case_ids}})
WHERE country = ANY({{country}})
\`\`\`

### \`date_range\` → \`BETWEEN {{param_from}} AND {{param_to}}\`

Single parameterName expands to two params with \`_from\` and \`_to\` suffixes:

\`\`\`sql
WHERE published_at BETWEEN {{period_from}}::date AND {{period_to}}::date
WHERE created_at >= {{period_from}}::timestamp AND created_at <= {{period_to}}::timestamp
\`\`\`

**Default values** accept absolute \`YYYY-MM-DD\` strings *or* **relative expressions** resolved at query time:

| Example | Meaning |
| --- | --- |
| \`now\` | today |
| \`now-7d\` | 7 days ago |
| \`now-1m\` | 1 month ago (calendar-aware) |
| \`now-1y\` | 1 year ago |
| \`now-30mi\` | 30 minutes ago |

Units (case-sensitive): \`s\` second · \`mi\` minute · \`h\` hour · \`d\` day · \`w\` week · \`m\` month · \`y\` year. Sub-day units (s/mi/h) bind as timestamps; the rest bind as dates. Cache keys include the resolved date, so widgets with relative defaults refresh as time advances.

**Examples:** a "Last 7 days" default — \`{ "from": "now-7d", "to": "now" }\`. A "Last 30 days (excluding today)" default — \`{ "from": "now-30d", "to": "now-1d" }\`.

### \`text\` → \`ILIKE {{param}}\`

Value auto-wrapped with \`%..%\`, special chars (\`%\`, \`_\`) escaped:

\`\`\`sql
WHERE title ILIKE {{search_text}}
WHERE (title ILIKE {{q}} OR description ILIKE {{q}})
\`\`\`

### Type casting

\`\`\`sql
{{story_id}}::integer    {{date_from}}::timestamp    {{amount}}::numeric
\`\`\`

### Pagination (table widgets)

\`\`\`sql
LIMIT {{__limit__}} OFFSET {{__offset__}}
\`\`\`

Set \`pageSize\` in widget config to enable. Variables: \`{{__limit__}}\`, \`{{__offset__}}\`, \`{{__page__}}\`, \`{{__page_size__}}\`.

### SQL Constraints

- **SELECT only** (INSERT/UPDATE/DELETE/DROP blocked)
- **64 KB** max query text
- **60 second** timeout
- **10,000 row** limit
- All \`{{placeholders}}\` must match defined filters or pagination variables

---

## Configuring Filters (UI)

Filters are managed in the **Edit Dashboard** mode via the filter panel on the left sidebar.

### Adding a filter

1. Enter edit mode (click **Edit Dashboard**)
2. Click **Add Filter** in the filter panel
3. Fill in the filter settings:
   - **Title** — Display label shown to users (e.g., "Country", "Date Range", "Search")
   - **Parameter Name** — SQL identifier used in \`{{placeholder}}\` (e.g., \`country\`). Must be letters, numbers, and underscores only, starting with a letter or underscore.
   - **Type** — Choose the UI control:
     - **Select** — Single-value dropdown
     - **Multi Select** — Multi-value dropdown with checkboxes
     - **Date Range** — Date range picker with start/end
     - **Text** — Free-text search input
   - **Filter SQL** — SQL query that returns the dropdown options (for Select and Multi Select types). First column = value, second column = display label. Leave empty for Text and Date Range.
   - **Default Value** — Pre-selected value when the dashboard first loads. For **Date Range** filters, each boundary accepts either an absolute \`YYYY-MM-DD\` date or a relative expression like \`now\`, \`now-7d\`, \`now-1m\` — see the SQL section above for the full syntax.
   - **Allow All** (checkbox) — Adds an "All" option to Select/Multi Select dropdowns. When selected, all values are included.
   - **Hidden** (checkbox) — Hides the filter from users. The server injects the default value automatically. Used for security/data isolation.
   - **Scope** — Dashboard-level (affects all widgets) or Widget-level (affects only one widget, selected from dropdown)

4. **Drag and drop** filters to reorder them. The display order also determines cascading priority: filters higher in the list are resolved first, and their values can be referenced in filters below.

### Cascading filters

Dependency rules differ by filter scope:

**Dashboard-level filters** are resolved strictly top-to-bottom. A dashboard filter at position N can use \`{{parameterName}}\` placeholders only from dashboard filters at positions 1…N-1.
- ✅ Dashboard filter at position 3 can reference dashboard filters at positions 1 and 2
- ❌ Dashboard filter at position 3 cannot reference a dashboard filter at position 4 or lower

**Widget-level filters** can reference **any** dashboard-level filter regardless of position, but cannot reference other widget-level filters.
- ✅ A widget filter can use \`{{case_ids}}\` or \`{{period}}\` (dashboard filters) in its SQL
- ❌ A widget filter cannot reference another widget filter

**The one rule that applies to both:** Two filters cannot reference each other — this creates a **circular dependency** that causes both to fail to resolve and the dashboard to break.

**⚠️ Circular dependency — most common cause of dashboard failure:**

If filter A's SQL uses \`{{filter_b}}\` and filter B's SQL uses \`{{filter_a}}\`, neither value can be resolved. The dashboard will silently produce no results or error. Fix by choosing a single direction: make filter B depend on filter A (or vice-versa), never both.

**Example (created automatically on new dashboards):**

1. **global_case_ids** (hidden, position 1): \`SELECT id, name FROM narrative WHERE workspace_id = 1 AND id IN (1, 2, 3) ORDER BY id DESC\` with "Allow All" enabled — admin must update the \`id IN (...)\` list and \`workspace_id\` for the customer
2. **case_ids** (visible, position 2): \`SELECT id, name FROM narrative WHERE id = ANY({{global_case_ids}}) ORDER BY id DESC\` with "Allow All" enabled

Filter 2 depends on filter 1 (correct — one direction only). Filter 1 does not reference filter 2. When \`global_case_ids\` resolves, its values flow into \`case_ids\` options query. The user sees only the \`case_ids\` dropdown.

### Hidden filters (data isolation)

Hidden filters enforce restrictions that users cannot see or modify:

- Check **Hidden** in the filter editor
- Set a **Filter SQL** that returns the allowed values
- Enable **Allow All** so all results are selected by default — **this is mandatory for hidden filters**: without it the filter has no selected value on load, which causes every widget that references \`{{parameterName}}\` to return no data
- The server injects these values automatically; user-supplied values are **ignored**

The default \`global_case_ids\` filter is the primary data isolation mechanism. The admin must update its SQL to use the correct \`workspace_id\` for the customer.

### Widget-level filters

To scope a filter to a single widget:
1. In the filter editor, change **Scope** from "Dashboard" to a specific widget
2. The filter will appear in that widget's header instead of the main filter bar
3. Widget-level filters can reference dashboard-level filters in their SQL

---

## Caching

Caching stores widget query results so repeated views load instantly instead of re-running SQL every time.

### How it works

1. When a cached widget is loaded, the backend looks up results by a **cache key** built from the widget ID, current filter values, and page number.
2. If a matching entry exists and hasn't expired, the cached result is returned — the SQL query is skipped entirely.
3. The \`cached: true\` flag in the response tells you a cache hit occurred.

Different filter values or page numbers produce different cache entries, so each unique combination is cached independently.

### Enabling caching

Set \`cache_ttl\` (seconds) in the widget config. A value of \`0\` (default) means caching is disabled.

\`\`\`json
{ "cache_ttl": 300 }
\`\`\`

### Cache types

Choose a \`cache_type\` to control *how* the cache behaves on expiry.

#### \`"blocking"\` (default)

The simplest mode. On a cache **hit**, return the cached result. On a cache **miss** (first load or after \`cache_ttl\` expires), execute the query, wait for results, cache them, and return.

\`\`\`json
{ "cache_ttl": 300, "cache_type": "blocking" }
\`\`\`

**Best for:** widgets where freshness matters and occasional slow loads are acceptable.

#### \`"stale_while_revalidate"\`

On a cache hit, the cached result is returned **immediately** — even if it's stale. If the freshness window (half of \`cache_ttl\`) has passed, a background task refreshes the cache so the *next* request gets updated data. On a cache miss, behaves like \`"blocking"\`.

\`\`\`json
{ "cache_ttl": 300, "cache_type": "stale_while_revalidate" }
\`\`\`

**Best for:** dashboards viewed frequently by users who prefer instant loads over absolute freshness. The data is at most one refresh cycle behind.

#### \`"background_update"\`

The cache is refreshed automatically on a timer, even when no one is viewing the dashboard. A background task runs every \`cache_refresh_interval\` seconds (defaults to \`cache_ttl\` if omitted) and re-executes the query with **default filter values and no pagination**. User requests are served from cache like \`"blocking"\`.

**Minimum refresh interval: 3600 seconds (1 hour).** Every refresh re-runs the query whether or not anyone is viewing the dashboard, so the effective interval (\`cache_refresh_interval\`, or \`cache_ttl\` when it is omitted) must be at least \`3600\` — the widget editor and the API reject lower values. Keep \`cache_ttl\` ≥ \`cache_refresh_interval\` so the cache does not expire between refreshes. If you need fresher data, use \`"stale_while_revalidate"\` instead: it only refreshes when someone actually views the widget.

\`\`\`json
{ "cache_ttl": 7200, "cache_type": "background_update", "cache_refresh_interval": 3600 }
\`\`\`

**Best for:** dashboards displayed on wall screens, monitoring dashboards, or any widget that should always show fresh data the moment someone opens it — without waiting for the first query to execute.

**Notes:**
- Background refresh uses default filter values only (page 1, no user-specific filter overrides). User-specific filter combinations are cached normally on first access.
- Admins can still force-invalidate from the widget card menu — this also resets the background refresh timer.

### General notes

- Cache only works in **view mode**. Edit mode / SQL preview always runs fresh queries.
- Admins can manually invalidate a widget's cache from the widget card menu (⋯ → Invalidate cache).
- Changing a filter or the widget's SQL automatically invalidates relevant cache entries.

---

## URL Sharing

Filter values stored as URL params with \`f_\` prefix:
\`\`\`
/custom-dashboards/{id}?f_case_ids=["87080","86949"]&f_period={"from":"2025-01-01","to":"2025-12-31"}
\`\`\`
Hidden filter values excluded from URL.

## Sharing a Dashboard Externally

The **Share** button on the dashboard page creates a share link, the same way cases and reports are shared:
- Optional validity period (otherwise the link lives two years) and optional password.
- The link opens \`/custom-dashboards/{id}/share/login?h=...\` — the viewer enters the password (if any) and lands on a read-only copy of the dashboard with its filters.
- A share link is never a super admin: hidden filters stay hidden and their defaults apply server-side, SQL is never exposed, and the token only works for the dashboard it was issued for.
- Before sharing, make sure the hidden \`global_case_ids\` filter restricts the data to what the external audience may see.
- Click actions of type \`set_filter\` work for external viewers. A \`url\` click action or a link column that points into the app (e.g. the search page) opens the app login page for them, so prefer external URLs on dashboards meant for sharing.

---

## Downloading Widgets as Images

Every widget with rendered content has a **⋮ (kebab) menu** in its header for exporting the widget as an image. No configuration is required — the menu appears automatically in both view and edit mode.

- **Chart widgets** (\`bar\`, \`line\`, \`pie\`, \`map\`, \`word_cloud\`) offer **Download as SVG** (true vector) and **Download as JPEG** (raster).
- **All other widgets** (\`table\`, \`metric\`, \`card_grid\`, \`opinions\`, \`text\`) offer **Download as JPEG** only, since they are HTML-based and have no vector source.

The downloaded file is named after the widget title (falling back to the widget ID if untitled). The menu is hidden while a widget is loading, errored, or empty.

---

## Troubleshooting

| Problem | Solution |
|---|---|
| Widget shows "Internal server error" | Check SQL syntax. Admins see detailed errors. |
| Widget shows no data | Check filter values, hidden filter defaults, data isolation filter |
| Filter dropdown empty | Check filter SQL query. Ensure it references correct parent filters. |
| Filter doesn't update widget | Widget SQL must use \`{{parameterName}}\` matching the filter |
| "Query exceeded maximum execution time" | Optimize query (indexes, LIMIT, simpler JOINs) |
| "Undefined filter variables" | All \`{{params}}\` must match defined filters |
| Dashboard slow | Enable caching (\`cache_ttl\`) |
| Filters don't cascade | Drag filters to reorder — higher position filters resolve first |
| Dashboard broken / no data after adding filters | Check for circular dependencies: if filter A references filter B and filter B references filter A, neither resolves. Fix by making the dependency one-directional. |
| Cross-workspace data visible | Update the hidden \`global_case_ids\` filter SQL with correct workspace_id |
| Widget-level filter not applying | Check filter scope is set to the correct widget |
`;

export function downloadConfigGuide(): void {
  const blob = new Blob([CONFIG_GUIDE_CONTENT], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = CONFIG_GUIDE_FILENAME;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}